import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { SupplierSelect } from "../../components/SupplierSelect";
import { Boxes, Search, Plus, Package, Layers, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate, money, SUB_INV_CATEGORIES } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";

const CURRENCIES = ["OMR", "USD", "AED"];
const SOURCES = ["AUTO — RMA", "MANUAL"];

function catClass(c) {
  return {
    "RMA APPROVED": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "OUT OF WARRANTY": "bg-slate-100 text-slate-700 border-slate-200",
    "CUSTOMER INDUCED DAMAGE": "bg-amber-50 text-amber-700 border-amber-200",
    "DEAD ON ARRIVAL": "bg-rose-50 text-rose-700 border-rose-200",
    "SUPPLIER REJECTED": "bg-purple-50 text-purple-700 border-purple-200",
    "OTHER": "bg-blue-50 text-blue-700 border-blue-200",
  }[c] || "bg-slate-100 text-slate-700 border-slate-200";
}

export default function SubInventory() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "WARRANTY_STAFF");
  const [list, setList] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [currency, setCurrency] = useState("");
  const [editItem, setEditItem] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (category) p.set("category", category);
    if (source) p.set("source", source);
    if (supplierFilter) p.set("supplier_id", supplierFilter);
    if (currency) p.set("currency", currency);
    const { data } = await api.get(`/admin/sub-inventory?${p}`);
    setList(data);
  };
  const loadStats = async () => { const { data } = await api.get("/admin/sub-inventory/stats"); setStats(data); };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search, category, source, supplierFilter, currency]);
  useEffect(() => { loadStats(); }, []);

  const refresh = () => { load(); loadStats(); };
  const currencyCards = stats?.currencies?.filter((c) => c.item_count > 0 || CURRENCIES.includes(c.currency)) || [];

  return (
    <AdminLayout title="Sub Inventory">
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {!stats ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) : (
          <>
            <StatCard icon={Package} color="text-blue-600 bg-blue-50" value={stats.total_items} label="Total Items" />
            <StatCard icon={Layers} color="text-slate-600 bg-slate-100" value={stats.total_qty} label="Total Quantity" />
            {currencyCards.map((cc) => (
              <div key={cc.currency} data-testid={`subinv-cost-${cc.currency}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">{cc.currency} Total Cost</div>
                <div className="font-display text-base font-bold text-slate-900">{money(cc.total_cost, cc.currency)}</div>
                <div className="text-xs text-slate-400">{cc.item_count} item{cc.item_count !== 1 ? "s" : ""}</div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="subinv-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product, part, serial, RMA, supplier, issue, remark..." className="pl-9" />
        </div>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full lg:w-52" data-testid="subinv-category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{SUB_INV_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full lg:w-44" data-testid="subinv-source-filter"><SelectValue placeholder="All Sources" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Sources</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={currency || "all"} onValueChange={(v) => setCurrency(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full lg:w-32" data-testid="subinv-currency-filter"><SelectValue placeholder="Currency" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Currencies</SelectItem>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="w-full lg:w-52"><SupplierSelect value={supplierFilter} onChange={setSupplierFilter} placeholder="All Suppliers" testId="subinv-supplier-filter" /></div>
        {canManage && <Button data-testid="add-subinv-btn" onClick={() => setEditItem({})} className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Add Item</Button>}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Product</TableHead><TableHead>Part</TableHead><TableHead>Serial</TableHead><TableHead className="text-center">Qty</TableHead>
                <TableHead>Category</TableHead><TableHead>Source</TableHead><TableHead>Cost/Unit</TableHead><TableHead>Total</TableHead>
                <TableHead>Date</TableHead><TableHead>RMA</TableHead><TableHead>Supplier</TableHead><TableHead>Issue</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list === null && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={13}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
              {list?.map((it) => (
                <TableRow key={it.id} data-testid={`subinv-row-${it.id}`} className="hover:bg-blue-50/40">
                  <TableCell className="max-w-[150px] truncate text-sm font-medium text-slate-900">{it.product_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{it.part_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{it.serial_number || "—"}</TableCell>
                  <TableCell className="text-center text-sm">{it.qty}</TableCell>
                  <TableCell><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${catClass(it.category)}`}>{it.category}</span></TableCell>
                  <TableCell><span className={`text-xs font-medium ${it.source === "AUTO — RMA" ? "text-blue-600" : "text-slate-500"}`}>{it.source}</span></TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{money(it.cost_per_unit, it.cost_currency)}</TableCell>
                  <TableCell className="font-mono text-xs font-semibold text-slate-900">{money(it.total_cost, it.cost_currency)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(it.date)}</TableCell>
                  <TableCell>{it.rma_number ? <button data-testid={`subinv-rma-${it.rma_number}`} onClick={() => navigate(`/admin/warranty/claims/${it.claim_id}`)} className="font-mono text-xs font-semibold text-blue-700 hover:underline">{it.rma_number}</button> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-sm text-slate-600">{it.supplier_name || "—"}</TableCell>
                  <TableCell className="max-w-[140px] truncate text-sm text-slate-500">{it.issue || "—"}</TableCell>
                  <TableCell className="text-right">{canManage && <Button size="sm" variant="ghost" data-testid={`subinv-edit-${it.id}`} onClick={() => setEditItem(it)} className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {list?.length === 0 && <EmptyState icon={Boxes} title="No inventory items" description="Items appear automatically from approved RMAs (not shipped to supplier), or add one manually." testId="subinv-empty" />}
      </div>
      {list && <div className="mt-3 text-sm text-slate-400">{list.length} item{list.length !== 1 ? "s" : ""}</div>}

      <ItemDialog item={editItem} onClose={() => setEditItem(null)} onSaved={refresh} />
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, color, value, label }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${color}`}><Icon className="h-4 w-4" /></div>
      <div className="font-display text-lg font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function ItemDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const isEdit = item && item.id;
  useEffect(() => {
    if (item) setForm({
      product_name: item.product_name || "", part_name: item.part_name || "", serial_number: item.serial_number || "",
      qty: item.qty || 1, date: item.date ? item.date.slice(0, 10) : "", issue: item.issue || "",
      category: item.category || "OTHER", cost_per_unit: item.cost_per_unit || "", cost_currency: item.cost_currency || "OMR",
      remark: item.remark || "",
    });
  }, [item?.id, item]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.product_name) { toast.error("Product name is required"); return; }
    try {
      if (isEdit) await api.put(`/admin/sub-inventory/${item.id}`, { ...form, qty: Number(form.qty) || 1 });
      else await api.post("/admin/sub-inventory", { ...form, qty: Number(form.qty) || 1 });
      toast.success(isEdit ? "Item updated" : "Item added");
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="subinv-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Inventory Item" : "Add Inventory Item"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="mb-1.5 block text-sm font-medium">Product Name *</Label><Input data-testid="subinv-product" value={form.product_name || ""} onChange={set("product_name")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Part Name</Label><Input data-testid="subinv-part" value={form.part_name || ""} onChange={set("part_name")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Serial Number</Label><Input data-testid="subinv-serial" value={form.serial_number || ""} onChange={set("serial_number")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Quantity</Label><Input type="number" min="1" data-testid="subinv-qty" value={form.qty || 1} onChange={set("qty")} /></div>
          <div><Label className="mb-1.5 block text-sm font-medium">Date</Label><Input type="date" data-testid="subinv-date" value={form.date || ""} onChange={set("date")} /></div>
          <div>
            <Label className="mb-1.5 block text-sm font-medium">Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="subinv-category"><SelectValue /></SelectTrigger>
              <SelectContent>{SUB_INV_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="mb-1.5 block text-sm font-medium">Cost per Unit</Label><Input type="number" step="0.001" data-testid="subinv-cost" value={form.cost_per_unit || ""} onChange={set("cost_per_unit")} /></div>
          <div>
            <Label className="mb-1.5 block text-sm font-medium">Currency</Label>
            <Select value={form.cost_currency} onValueChange={(v) => setForm({ ...form, cost_currency: v })}>
              <SelectTrigger data-testid="subinv-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="mb-1.5 block text-sm font-medium">Issue</Label><Input data-testid="subinv-issue" value={form.issue || ""} onChange={set("issue")} /></div>
          <div className="sm:col-span-2"><Label className="mb-1.5 block text-sm font-medium">Remark</Label><Textarea rows={2} data-testid="subinv-remark" value={form.remark || ""} onChange={set("remark")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="save-subinv-btn" className="bg-blue-600 hover:bg-blue-700">{isEdit ? "Save Changes" : "Add Item"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
